'use client';

import { useQuery } from '@tanstack/react-query';
import { AccountInfo, Connection, PublicKey } from '@solana/web3.js';
import {
	VAULT_PROGRAM_ID,
	VaultClient,
	VaultDepositor,
	VaultDepositorAccount,
} from '@drift-labs/vaults-sdk';
import { COMMON_UTILS } from '@drift/common';
import { vaultDataDecoder } from 'src/utils/programAccounts/decoders';
import { UiVaults } from 'src/constants/vaults';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import { VaultDepositorAccountsLookup } from '../types';
import { VAULT_REFETCH_INTERVAL } from '../consts';
import { vaultQueryKeys } from '../vaultQueryKeys';
import { QueryDisabledError } from 'src/utils/error/QueryDisabledError';

function decodeVaultDepositorAccounts(
	vaultDepositorAccounts: AccountInfo<Buffer>[],
	vaultClient: VaultClient
) {
	return vaultDepositorAccounts.map((vaultDepositorAccount) => {
		if (!vaultDepositorAccount) return null;
		const vaultDepositorAccountData =
			vaultClient.program.account.vaultDepositor.coder.accounts.decode(
				'vaultDepositor',
				vaultDepositorAccount.data
			);
		return vaultDepositorAccountData as VaultDepositor;
	});
}

async function fetchVaultDepositorAccounts(
	connection: Connection,
	vaultClient: VaultClient,
	authority: PublicKey,
	vaultPubkeys: string[]
): Promise<VaultDepositorAccountsLookup> {
	const vaultDepositorPubkeys = vaultPubkeys.map((vaultPubkey) => {
		return VaultDepositorAccount.getAddressSync(
			VAULT_PROGRAM_ID,
			new PublicKey(vaultPubkey),
			authority
		);
	});

	const vaultDepositorAccounts = (
		await COMMON_UTILS.getMultipleAccountsInfoChunked(
			connection,
			vaultDepositorPubkeys
		)
	).filter(Boolean) as AccountInfo<Buffer>[];

	const decodedAccounts = decodeVaultDepositorAccounts(
		vaultDepositorAccounts,
		vaultClient
	);

	const existingVaultDepositorAccounts = decodedAccounts.filter((acc) => !!acc);

	if (existingVaultDepositorAccounts.length === 0) {
		return {};
	}

	const vaultPubkeysToFetch = existingVaultDepositorAccounts.map(
		(vaultDepositor) => vaultDepositor.vault
	);

	const vaultAccounts =
		await connection.getMultipleAccountsInfo(vaultPubkeysToFetch);

	const lookup: VaultDepositorAccountsLookup = {};

	for (let i = 0; i < existingVaultDepositorAccounts.length; i++) {
		const vaultDepositor = existingVaultDepositorAccounts[i];
		const vaultData = vaultAccounts[i];

		if (!vaultData) {
			continue;
		}

		const vault = vaultDataDecoder(vaultData.data, vaultClient.program);
		const vaultPubkey = vaultDepositor.vault.toString();

		lookup[vaultPubkey] = {
			vaultDepositor,
			vault,
		};
	}

	return lookup;
}

export function useVaultDepositorAccounts() {
	const authority = useCurrentAuthority();
	const connection = useDriftStore((s) => s.connection.current);
	const vaultClient = useVaultsStore((s) => s.vaultClient);
	const vaultConfigsLoaded = useVaultsStore((s) => s.areVaultConfigsLoaded);

	const vaultPubkeys = vaultConfigsLoaded
		? UiVaults.getNativeVaults().map((vault) => vault.vaultPubkeyString)
		: [];

	const enabled =
		!!authority && !!connection && !!vaultClient && vaultPubkeys.length > 0;

	return useQuery({
		queryKey: vaultQueryKeys.vaultDepositorAccounts(authority?.toString()),
		queryFn: async () => {
			if (!enabled) {
				throw new QueryDisabledError();
			}
			return fetchVaultDepositorAccounts(
				connection!,
				vaultClient!,
				authority!,
				vaultPubkeys
			);
		},
		enabled,
		staleTime: VAULT_REFETCH_INTERVAL,
		refetchInterval: VAULT_REFETCH_INTERVAL,
	});
}
